import {
    RoleI,
    ApiDataI,
    RequestBodyI,
    RequestDataI,
    ClientError,
    JsonRequestResponse,
    UserRoleName,
    LoginAuthenticationResponse,
    AuthorizationDataI,
} from "./types"
import packageJs from "../package.json"

export default new class {
    readonly AUTH = '_auth_'
    readonly HOST = '_host_'
    readonly KEEP_USER_LOGGED_IN_FLAG = '_keep_user_logged_in_'
    userRoles: RoleI[] = []
    host = ''
    token = ''
    username = ''
    userID = -1
    personID = -1
    tokenExpiryDate = ''
    sessionAvailable = false
    canPersistLogin: boolean = false
    interceptRequest: (req: RequestDataI) => RequestDataI
    interceptResponse: (req: RequestDataI, res: JsonRequestResponse) => JsonRequestResponse
    constructor() {
        this.loadState()
        this.interceptRequest = (req: RequestDataI) => req
        this.interceptResponse = (_, res: JsonRequestResponse) => res
    }
    /**
     * This will return the current logged in user's name
     * @returns 
     */
    getUsername() { return this.username }

    /**
     * Get the logged in user's person id
     * @returns 
     */
    getProviderID() { return this.personID }

    /**
     * Returns userID of currently logged in user
     * @returns 
     */
    getUserID() { return this.userID }
    
    /**
     * Returns URL of API host
     * @returns 
     */
    getHost() { return this.host }

    /**
     * Configure API host URL i.e. http://localhost:3000 or https://emr.malawi.com 
     * @param host 
     */
    setHost(host: string) {
        this.host = host
        localStorage.setItem(this.HOST, this.host)
    }

    /**
     * Generic function for running API endpoints.
     * @param relativeUrl 
     * @param data 
     * @param baseUrl 
     * @returns 
     */
    async restRequest<T = any>(relativeUrl: string, data: RequestBodyI, baseUrl='api/v1'): Promise<JsonRequestResponse<T>> {
        let requestData: RequestDataI = {
            url: `${this.host}/${baseUrl}/${relativeUrl}`,
            config: {
                ...data,
                mode: 'cors',
                headers: {
                    'Authorization': this.token,
                    'Content-Type': 'application/json'
                }
            }
        }
        const res: JsonRequestResponse = { ok: false }
        // Run callback before making API request to allow for modifications to the original request object
        requestData = this.interceptRequest(requestData)
        try {
            const req = await fetch(requestData.url, requestData.config)
            const statusResponse = req.status
            res.httpStatusResponse = statusResponse;
            if(statusResponse === 204) {
                return this.interceptResponse(requestData, res);
            }

            res.data = await req.json()
            if ([200, 201, 202].includes(statusResponse)) {
                res.ok = true;
                return this.interceptResponse(requestData, res)
            }
            switch(statusResponse) {
                case 400:
                    res.clientErrorType = ClientError.BAD_REQUEST
                    break;
                case 401:
                    res.clientErrorType = ClientError.AUTHENTICATION_ERROR
                    break;
                case 404:
                    res.clientErrorType = ClientError.NOT_FOUND
                    break;
                case 409: 
                    res.clientErrorType = ClientError.RECORD_CONFLICT
                    break;
                case 422:
                    res.clientErrorType = ClientError.ENTITY_ERROR
                    break;
                case 500:
                    res.clientErrorType = ClientError.SERVER_ERROR
                    break;
                case 502:
                    res.clientErrorType = ClientError.GATE_WAY_ERROR
                    break; 
                default:
                    res.clientErrorType = ClientError.GENERAL_REQUEST_ERROR
                    break;
            }
            res.errorMessage = Array.isArray(res.data?.errors) 
                ? res.data?.errors.join(',') 
                : res.clientErrorType || `Error code ${res.httpStatusResponse}`
        } catch(e) {
            if (/NetworkError|Failed to fetch/i.test(`${e}`)) {
                res.clientErrorType = ClientError.NO_CONNECTION
                res.errorMessage = "Unable to connect to server/network"
            } else {
                res.clientErrorType = ClientError.INTERNAL_ERROR
                res.errorMessage = `${e}`
            }
        }
        return this.interceptResponse(requestData, res)
    }

    /**
     * Authentication methord that creates a temporary session on the client side after a successful login
     * @param username
     * @param password
     * @returns
     */
    async login(username: string, password: string, canPersistLogin=this.canPersistLogin) {
        this.sessionAvailable = false
        const res = await this.postJson<LoginAuthenticationResponse>('auth/login', { username, password })
        if (res.ok) {
            this.canPersistLogin = canPersistLogin
            this.username = username
            this.startSession(res.data!.authorization)
            return res
        }
        this.clearState()
        return res
    }


    /**
     * Starts a new session by saving authentication data and updating the session state.
     * @param data - The authorization data containing token, expiry time, user details, and roles.
     */
    startSession(data: AuthorizationDataI) {
        this.tokenExpiryDate = data.expiry_time
        this.personID = data.user.person_id
        this.userID = data.user.user_id
        this.userRoles = data.user.roles
        this.token = data.token
        this.sessionAvailable = true
        this.saveState()
    }

    /**
     * Verifies a password reset token by making a POST request to the API.
     * If the token is valid, starts a new session with the returned authorization data.
     * @param token - The reset token to verify.
     * @returns A boolean indicating whether the token verification was successful.
     */
    async verifyResetToken(token: string) {
        const res = await this.postJson<LoginAuthenticationResponse>(`auth/reset_password?code=${token}`, {})
        if (res.ok && res.data) {
            this.startSession(res.data.authorization);
            return true;
        }
        this.clearState()
        return false;
    }

    /**
     * Clears login session information on the client side
     */
    logout() {
        this.token = ''
        this.userID = -1
        this.personID = -1
        this.username = ''
        this.userRoles = []
        this.tokenExpiryDate = ''
        this.sessionAvailable = false
        this.clearState()
    }

    /**Simple check to verify login session */
    isLoggedIn() { return this.sessionAvailable }

    /**
     * Validates provided roles against roles assigned to the current login user
     * @param roles
     * @returns
    */
    userHasRoles(roles: UserRoleName[]) {
        const sanitize = (txt: string) => txt.toLowerCase().trim()
        const sanitizedRoles = roles.map(r => sanitize(r))
        return this.userRoles.some((role) => sanitizedRoles.includes(sanitize(role.role)))
    }

    /**
     * Perfoms GET http requests with optional parameters
     * @param url 
     * @param params 
     * @returns 
     */
    getJson<T>(url: string, params: Record<string, any>={}) {
        const urlParams = Object.keys(params).map(k => `${k}=${params[k]}`).join('&')
        const fullUrl = urlParams.length ? `${url}?${urlParams}` : url
        return this.restRequest<T>(fullUrl, { method: "GET" })
    }

    /**
     * Perfoms POST http requests with provided data
     * @param url 
     * @param data 
     * @returns 
     */
    postJson<T>(url: string, data: any) {
        return this.restRequest<T>(url, {
            method: "POST",
            body: JSON.stringify(data)
        })
    }

   /**
     * Perfoms PUT http requests with provided data
     * @param url 
     * @param data 
     * @returns 
     */
    putJson<T>(url: string, data: any) {
        return this.restRequest<T>(url, { 
            method: "PUT",
            body: JSON.stringify(data)
        })
    }

    /**
     * Perfoms DELETE http requests with provided data
     * @param url 
     * @param data 
     * @returns 
     */
    void<T>(url: string, params={}) {
        return this.restRequest<T>(url, { 
            method: "DELETE", 
            body: JSON.stringify(params) 
        })
    }

    /**
     * Method for validating expiration status of a token
     * @returns 
     */
    tokenHasExpired() {
        return this.tokenExpiryDate ? new Date() > new Date(this.tokenExpiryDate) : true 
    }

    /**
     * Perfoms a healthcheck operation to verify if the API is up
     * @returns 
     */
    async apiOk() {
        const req = await this.getJson<Record<'status', 'up' | 'down'>>('_health')
        return req.ok && `${req?.data?.status}`.toLowerCase() === 'up'
    }

    /**
     * Gets current date from the API
     * @returns 
     */
    async getDate() {
        const req = await this.getJson('current_time')
        if (req.ok) return req.data
    }

    /**
     * Gets current API version
     * @returns 
     */
    async getVersion() {
        const req = await this.getJson<Record<string, string>>('version')
        if (req.ok && req.data) return req?.data['System version']
    }

    /**
     * Caches authentication data
     */
    saveState() {
        const data = JSON.stringify({
            token: this.token,
            userID: this.userID,
            personID: this.personID,
            username: this.username,
            userRoles: this.userRoles,
            tokenExpiryDate: this.tokenExpiryDate
        })
        this.canPersistLogin
            ? localStorage.setItem(this.AUTH, data)
            : sessionStorage.setItem(this.AUTH, data)
        localStorage.setItem(this.KEEP_USER_LOGGED_IN_FLAG, `${this.canPersistLogin}`)
    }

    /**
     * Reads saved authentication state
     */
    loadState() {
        this.canPersistLogin = JSON.parse(localStorage.getItem(this.KEEP_USER_LOGGED_IN_FLAG)||`${this.canPersistLogin}`)
        const strData = this.canPersistLogin
            ? localStorage.getItem(this.AUTH)
            : sessionStorage.getItem(this.AUTH)
        const state: ApiDataI = JSON.parse(strData||'{}')
        this.host = localStorage.getItem(this.HOST)|| packageJs?.apiConfig?.host
        this.token = state?.token
        this.username = state?.username
        this.personID = parseInt(`${state?.personID}`)
        this.userID = parseInt(`${state?.userID}`)
        this.tokenExpiryDate = state?.tokenExpiryDate
        this.userRoles = state?.userRoles || [] as any
        this.sessionAvailable = Object.keys(state).length > 0 && !this.tokenHasExpired()
    }

    clearState() {
        localStorage.removeItem(this.AUTH)
        sessionStorage.removeItem(this.AUTH)
    }
}
